import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Star } from "lucide-react";

/**
 * Owner-only management modal for Featured Show entries. Lives outside
 * OwnerBar to keep that component from growing further — OwnerBar just
 * imports + renders this when its menu opens the FEATURED sub-panel.
 *
 * Behaviour:
 *   • Reads /api/site/featured on mount + when `open` flips true.
 *   • Add: shows a form; POST /site/featured; refresh list on success.
 *   • Edit: inline row expansion with the same form; PUT /{id}.
 *   • Delete: confirm-with-double-click; DELETE /{id}.
 *   • Reorder: up/down arrows + POST /site/featured/reorder.
 *   • "Pick from library" (channel/tape) auto-fills title + art URLs
 *     from the chosen record, so the owner can promote existing
 *     content without hand-copying URLs.
 *
 * Emits `crt:featured-updated` after every mutation so the Channel
 * Guide's FeaturedPanel refetches immediately without a page reload.
 */
export function OwnerFeaturedManager({
  open,
  onClose,
  apiBase,
  authHeaders,
  channels,
  videosByChannel,
}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // entry.id | "new" | null
  const [confirmDelete, setConfirmDelete] = useState(null); // entry.id
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form draft — held in state so pick-from-library can overwrite it.
  const empty = useMemo(() => ({
    title: "",
    tagline: "",
    backdrop_url: "",
    poster_url: "",
    target_channel_id: "",
    target_video_id: "",
    watch_now_label: "",
  }), []);
  const [draft, setDraft] = useState(empty);

  // Load list whenever the modal opens. Also called after mutations
  // via a manual refresh() below.
  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/site/featured`);
      if (res.ok) {
        const data = await res.json();
        setEntries(Array.isArray(data.entries) ? data.entries : []);
      }
    } catch (e) { /* leave list as-is on failure */ }
    finally { setLoading(false); }
  }, [apiBase]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // When switching the target channel, if a video was previously
  // selected but no longer exists on the new channel, drop the stale
  // video id. This keeps the two dropdowns coherent.
  useEffect(() => {
    if (!draft.target_video_id || !draft.target_channel_id) return;
    const list = videosByChannel && videosByChannel[draft.target_channel_id];
    if (!Array.isArray(list) || !list.find((v) => v.id === draft.target_video_id)) {
      setDraft((d) => ({ ...d, target_video_id: "" }));
    }
  }, [draft.target_channel_id, draft.target_video_id, videosByChannel]);

  const startEditing = (entry) => {
    setEditing(entry.id);
    setDraft({
      title: entry.title || "",
      tagline: entry.tagline || "",
      backdrop_url: entry.backdrop_url || "",
      poster_url: entry.poster_url || "",
      target_channel_id: entry.target_channel_id || "",
      target_video_id: entry.target_video_id || "",
      watch_now_label: entry.watch_now_label || "",
    });
    setError("");
  };

  const startAdd = () => {
    setEditing("new");
    setDraft(empty);
    setError("");
  };

  const cancel = () => {
    setEditing(null);
    setDraft(empty);
    setError("");
  };

  // Pick-from-library helpers — populate the draft using an existing
  // channel or tape. The owner can then tweak the tagline / label.
  const pickFromChannel = (channelId) => {
    const c = channels?.find((x) => x.id === channelId);
    if (!c) return;
    setDraft((d) => ({
      ...d,
      title: d.title || c.name || "",
      backdrop_url: d.backdrop_url || c.guide_poster_url || c.thumbnail_url || "",
      poster_url: d.poster_url || c.thumbnail_url || "",
      target_channel_id: c.id,
    }));
  };
  const pickFromTape = (channelId, videoId) => {
    const list = videosByChannel && videosByChannel[channelId];
    if (!Array.isArray(list)) return;
    const v = list.find((x) => x.id === videoId);
    if (!v) return;
    // Picking a tape is a strong "I want THIS specific content" signal
    // — OVERWRITE the descriptive fields with tape metadata. Previously
    // `d.title || v.title` kept whatever the channel-picker had already
    // filled, so the featured panel ended up showing generic channel
    // art even when the owner had selected a specific tape. Fall back
    // to the parent channel's art only when the tape has none of its
    // own (common for owner-imported tapes without TMDb backfill).
    const channel = channels?.find((c) => c.id === channelId);
    setDraft((d) => ({
      ...d,
      title: v.title || d.title || "",
      tagline: v.description || v.show_title || d.tagline || "",
      backdrop_url: v.backdrop_url || v.poster_url || (channel && channel.guide_poster_url) || d.backdrop_url || "",
      poster_url: v.poster_url || v.backdrop_url || (channel && channel.thumbnail_url) || d.poster_url || "",
      target_channel_id: channelId,
      target_video_id: v.id,
    }));
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const isNew = editing === "new";
      const url = isNew
        ? `${apiBase}/site/featured`
        : `${apiBase}/site/featured/${editing}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(authHeaders || {}) },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `${method} failed (${res.status})`);
      }
      setEditing(null);
      setDraft(empty);
      await refresh();
      window.dispatchEvent(new CustomEvent("crt:featured-updated"));
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entryId) => {
    setSaving(true);
    try {
      await fetch(`${apiBase}/site/featured/${entryId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...(authHeaders || {}) },
      });
      setConfirmDelete(null);
      await refresh();
      window.dispatchEvent(new CustomEvent("crt:featured-updated"));
    } finally {
      setSaving(false);
    }
  };

  const reorder = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= entries.length) return;
    const next = [...entries];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setEntries(next); // optimistic
    try {
      await fetch(`${apiBase}/site/featured/reorder`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(authHeaders || {}) },
        body: JSON.stringify({ ids: next.map((e) => e.id) }),
      });
      window.dispatchEvent(new CustomEvent("crt:featured-updated"));
    } catch (e) { /* revert not attempted — refresh() would sync */ }
  };

  if (!open) return null;

  const videosForTargetChannel = draft.target_channel_id
    ? (videosByChannel && videosByChannel[draft.target_channel_id]) || []
    : [];

  return (
    <div
      className="absolute top-12 right-0 w-[520px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_24px_rgba(85,255,85,0.25)] z-50"
      data-testid="featured-manager-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star size={16} />
          <div className="text-[13px] tracking-[0.28em]">┌─ ★ FEATURED ─┐</div>
        </div>
        <button onClick={onClose} data-testid="featured-manager-close" className="text-[var(--phosphor)]">
          <X size={16} />
        </button>
      </div>

      <div className="text-[11px] opacity-70 mb-3 leading-snug">
        Curate hero entries for the Channel Guide&apos;s right panel. Rotates every 8s.
        Empty list falls back to &quot;Coming Up Next&quot;.
      </div>

      {/* Existing entries list — with reorder + edit + delete */}
      {loading && <div className="text-[11px] opacity-70">Loading…</div>}
      {!loading && entries.length === 0 && editing !== "new" && (
        <div className="text-[11px] opacity-60 border border-dashed border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-3 mb-3">
          No featured entries yet. Add one to replace &quot;Coming Up Next&quot; with your promo.
        </div>
      )}
      {!loading && entries.map((e, i) => (
        <div key={e.id} className="border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2 mb-2 text-[11px]">
          {editing === e.id ? (
            <FeaturedForm
              draft={draft}
              setDraft={setDraft}
              channels={channels}
              videosByChannel={videosByChannel}
              videosForTargetChannel={videosForTargetChannel}
              pickFromChannel={pickFromChannel}
              pickFromTape={pickFromTape}
              submit={submit}
              cancel={cancel}
              saving={saving}
              error={error}
              isNew={false}
            />
          ) : (
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-0.5 pt-1 shrink-0">
                <button onClick={() => reorder(i, i - 1)} disabled={i === 0} data-testid={`featured-move-up-${i}`} className="opacity-70 hover:opacity-100 disabled:opacity-20"><ArrowUp size={12} /></button>
                <button onClick={() => reorder(i, i + 1)} disabled={i === entries.length - 1} data-testid={`featured-move-down-${i}`} className="opacity-70 hover:opacity-100 disabled:opacity-20"><ArrowDown size={12} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="tracking-[0.2em] truncate" data-testid={`featured-entry-title-${i}`}>{e.title}</div>
                {e.tagline && <div className="opacity-70 truncate mt-0.5">{e.tagline}</div>}
                <div className="opacity-55 mt-0.5">
                  {e.target_channel_id ? (channels?.find((c) => c.id === e.target_channel_id)?.name || "channel") : "no target"}
                  {e.target_video_id ? " · tape" : ""}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => startEditing(e)} data-testid={`featured-edit-${i}`} className="text-[10px] tracking-[0.2em] border border-[var(--phosphor)] px-1.5 py-0.5 hover:bg-[var(--phosphor)] hover:text-black">EDIT</button>
                {confirmDelete === e.id ? (
                  <button onClick={() => remove(e.id)} data-testid={`featured-confirm-delete-${i}`} className="text-[10px] tracking-[0.2em] border border-red-500 text-red-400 px-1.5 py-0.5 hover:bg-red-500 hover:text-black"><Trash2 size={10} className="inline mr-0.5" />SURE?</button>
                ) : (
                  <button onClick={() => setConfirmDelete(e.id)} data-testid={`featured-delete-${i}`} className="text-[10px] tracking-[0.2em] border border-red-500/60 text-red-400/70 px-1.5 py-0.5 hover:border-red-500 hover:text-red-400"><Trash2 size={10} /></button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add-new form OR add button */}
      {editing === "new" ? (
        <div className="border border-[var(--phosphor)] p-2 text-[11px]">
          <FeaturedForm
            draft={draft}
            setDraft={setDraft}
            channels={channels}
            videosByChannel={videosByChannel}
            videosForTargetChannel={videosForTargetChannel}
            pickFromChannel={pickFromChannel}
            pickFromTape={pickFromTape}
            submit={submit}
            cancel={cancel}
            saving={saving}
            error={error}
            isNew={true}
          />
        </div>
      ) : (
        !editing && (
          <button
            onClick={startAdd}
            data-testid="featured-add"
            className="w-full mt-2 border border-[var(--phosphor)] py-1.5 text-[12px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors flex items-center justify-center gap-1"
          >
            <Plus size={14} /> ADD FEATURED
          </button>
        )
      )}
    </div>
  );
}

// Extracted form component so add + edit share the same UI verbatim.
function FeaturedForm({ draft, setDraft, channels, videosForTargetChannel, pickFromChannel, pickFromTape, submit, cancel, saving, error, isNew }) {
  return (
    <div>
      <label className="block mb-2">
        <div className="tracking-[0.2em] opacity-80 mb-1">TITLE *</div>
        <input
          type="text"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          placeholder="Coming Soon · Season 2"
          data-testid="featured-form-title"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[12px]"
        />
      </label>
      <label className="block mb-2">
        <div className="tracking-[0.2em] opacity-80 mb-1">TAGLINE</div>
        <input
          type="text"
          value={draft.tagline}
          onChange={(e) => setDraft((d) => ({ ...d, tagline: e.target.value }))}
          placeholder="One-liner shown under the title"
          data-testid="featured-form-tagline"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[12px]"
        />
      </label>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <label className="block">
          <div className="tracking-[0.2em] opacity-80 mb-1">BACKDROP URL</div>
          <input
            type="text"
            value={draft.backdrop_url}
            onChange={(e) => setDraft((d) => ({ ...d, backdrop_url: e.target.value }))}
            placeholder="1920×1080 hero"
            data-testid="featured-form-backdrop"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[12px]"
          />
        </label>
        <label className="block">
          <div className="tracking-[0.2em] opacity-80 mb-1">POSTER URL</div>
          <input
            type="text"
            value={draft.poster_url}
            onChange={(e) => setDraft((d) => ({ ...d, poster_url: e.target.value }))}
            placeholder="500×750 portrait"
            data-testid="featured-form-poster"
            className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[12px]"
          />
        </label>
      </div>

      {/* Pick from library — dropdowns that auto-fill on select. */}
      <div className="border border-dashed border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2 mb-2">
        <div className="tracking-[0.2em] opacity-70 mb-1 text-[10px]">▾ PICK FROM LIBRARY (auto-fills empty fields)</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <div className="tracking-[0.18em] opacity-70 mb-1 text-[10px]">CHANNEL</div>
            <select
              value={draft.target_channel_id}
              onChange={(e) => { const v = e.target.value; if (v) pickFromChannel(v); else setDraft((d) => ({ ...d, target_channel_id: "", target_video_id: "" })); }}
              data-testid="featured-form-channel"
              className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-1 py-1 text-[11px]"
            >
              <option value="">— none —</option>
              {(channels || []).filter((c) => !c.virtual && !c.isSubChannel).map((c) => (
                <option key={c.id} value={c.id}>{c.numberLabel || c.number} · {c.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="tracking-[0.18em] opacity-70 mb-1 text-[10px]">TAPE (optional)</div>
            <select
              value={draft.target_video_id}
              onChange={(e) => { const v = e.target.value; if (v) pickFromTape(draft.target_channel_id, v); else setDraft((d) => ({ ...d, target_video_id: "" })); }}
              disabled={!draft.target_channel_id || videosForTargetChannel.length === 0}
              data-testid="featured-form-video"
              className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-1 py-1 text-[11px] disabled:opacity-40"
            >
              <option value="">— channel default —</option>
              {videosForTargetChannel.map((v) => (
                <option key={v.id} value={v.id}>{v.title}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <label className="block mb-3">
        <div className="tracking-[0.2em] opacity-80 mb-1">BUTTON LABEL</div>
        <input
          type="text"
          value={draft.watch_now_label}
          onChange={(e) => setDraft((d) => ({ ...d, watch_now_label: e.target.value }))}
          placeholder="WATCH NOW"
          data-testid="featured-form-label"
          className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] focus:border-[var(--phosphor)] outline-none px-2 py-1 text-[12px]"
        />
      </label>

      {error && <div className="text-red-400 text-[11px] mb-2" data-testid="featured-form-error">{error}</div>}

      <div className="flex gap-2">
        <button onClick={cancel} className="flex-1 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] py-1.5 text-[11px] tracking-widest hover:border-[var(--phosphor)] transition-colors" data-testid="featured-form-cancel">CANCEL</button>
        <button
          onClick={submit}
          disabled={saving || !draft.title.trim()}
          className="flex-1 border border-[var(--phosphor)] py-1.5 text-[11px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black disabled:opacity-40 transition-colors"
          data-testid="featured-form-save"
        >
          {saving ? "SAVING…" : (isNew ? "ADD" : "UPDATE")}
        </button>
      </div>
    </div>
  );
}
